import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { isTextTruncated } from '@ardoq/common-helpers';

type Measurement = {
  element: HTMLElement | null;
  text: string | null;
  lineClamp: number | undefined;
};

const isSameMeasurement = (a: Measurement, b: Measurement): boolean =>
  a.element === b.element && a.text === b.text && a.lineClamp === b.lineClamp;

const observeResize = (
  element: HTMLElement,
  onResize: VoidFunction
): ResizeObserver => {
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(element);
  return resizeObserver;
};

/**
 * Whether the text in `ref`'s element is cut off by its ellipsis or line clamp.
 * Re-measures before paint when the element resizes, is replaced, or its text
 * changes between renders.
 */
export const useIsTextTruncated = (
  ref: RefObject<HTMLElement | null>,
  lineClamp?: number
): boolean => {
  const [isTruncated, setIsTruncated] = useState(false);
  const lastMeasurement = useRef<Measurement | null>(null);
  const resizeObserver = useRef<ResizeObserver | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    const measurement: Measurement = {
      element,
      text: element?.textContent ?? null,
      lineClamp,
    };
    const previous = lastMeasurement.current;
    if (previous && isSameMeasurement(previous, measurement)) {
      return;
    }
    lastMeasurement.current = measurement;

    const measure = () =>
      setIsTruncated(element !== null && isTextTruncated(element, lineClamp));
    if (previous?.element !== element || previous?.lineClamp !== lineClamp) {
      resizeObserver.current?.disconnect();
      resizeObserver.current = element ? observeResize(element, measure) : null;
    }
    measure();
  });

  useLayoutEffect(
    () => () => {
      resizeObserver.current?.disconnect();
      resizeObserver.current = null;
      lastMeasurement.current = null;
    },
    []
  );

  return isTruncated;
};
